import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import SettingsIcon from '@material-ui/icons/Settings';
import { useMediaQuery } from '@material-ui/core';
import { setSidebarVisibility, useTranslate, DashboardMenuItem, MenuItemLink } from 'react-admin';
import Divider from '@material-ui/core/Divider';
import CardMedia from '@material-ui/core/CardMedia';
import { makeStyles } from '@material-ui/core/styles';
import jwt_decode from 'jwt-decode';
import { useHistory } from 'react-router-dom';

import SubMenu from './SubMenu';
import IconLink from './IconButtonLink';
import useTopMenu from './useTopMenu';
import { buildMenu, showDashboard, showLogo } from './menuConfig';

const { REACT_APP_LOGO_IMAGE_URL, REACT_APP_LOGO_URL, REACT_APP_TIMEOUT_SECONDS } = process.env;

const useStyles = makeStyles({
    media: {
        height: '16em',
    },
    logo: {
        height: '4em',
        width: '4em',
    },
});

const useInactivityTimer = (logoutFunction, timeoutDuration) => {
    useEffect(() => {
        let timer;

        const resetTimer = () => {
            clearTimeout(timer);
            timer = setTimeout(logoutFunction, timeoutDuration);
        };

        window.addEventListener('mousemove', resetTimer);
        window.addEventListener('keydown', resetTimer);

        resetTimer(); // Initialize timer

        return () => {
            clearTimeout(timer); // Clean up
            window.removeEventListener('mousemove', resetTimer);
            window.removeEventListener('keydown', resetTimer);
        };
    }, [logoutFunction, timeoutDuration]);
};

/**
 * Vertical sidebar menu.
 *
 * The navigation tree itself lives in menuConfig.js, shared with TopMenu.js.
 * This file only decides how to draw it.
 *
 * NOTE: the inactivity auto-logout timer lives here and this component stays
 * mounted in both menu layouts - Layout.js hides the sidebar with CSS rather
 * than unmounting it, so the timer keeps running in top-menu mode.
 */
const Menu = ({ onMenuClick, dense, logout }) => {
    const dispatch = useDispatch();
    const [openSections, setOpenSections] = useState({});
    const translate = useTranslate();
    const isXsmall = useMediaQuery(theme => theme.breakpoints.down('xs'));
    const open = useSelector(state => state.admin.ui.sidebarOpen);
    const topMenuActive = useTopMenu();
    useSelector(state => state.theme); // force rerender on theme change

    const classes = useStyles();
    const history = useHistory();

    const handleToggle = key => {
        setOpenSections(prev => Object.assign({}, prev, { [key]: !prev[key] }));
    };

    const decoded = localStorage.token ? jwt_decode(localStorage.token) : null;
    const branch_code = decoded ? decoded.branch_code : '';
    const level = decoded ? decoded.level : 0;
    const userid = decoded ? decoded.id : 0;
    const code = decoded ? decoded.code : '';

    const TimeoutSeconds = REACT_APP_TIMEOUT_SECONDS && REACT_APP_TIMEOUT_SECONDS > 0
        ? REACT_APP_TIMEOUT_SECONDS
        : 300;

    useInactivityTimer(() => {
        if (Number(level) === 1) {
            // kiosk users are never logged out on idle
        } else {
            console.log('User has been logged out due to inactivity');
            localStorage.removeItem('username');
            localStorage.removeItem('token');
            localStorage.clear();
            sessionStorage && sessionStorage.clear();
            Promise.resolve();
            history.push('/login');
        }
    }, TimeoutSeconds * 1000);

    // Force sidebar closed for kiosk users
    if (Number(level) === 1 && open) {
        dispatch(setSidebarVisibility(false));
    }

    if (Number(level) === 1) { return null; }

    // In top-menu mode the links are drawn in the bar under the app bar.
    // This component still mounts (keeping the idle timer alive) but renders
    // nothing; Layout.js hides the sidebar around it. Both use useTopMenu so
    // the sidebar can never be shown while this returns null.
    if (topMenuActive) { return null; }

    const context = { level, code, branch_code, userid };
    const sections = buildMenu(context);

    return (
        <div>
            {showLogo(context) && (
                <a href={`${REACT_APP_LOGO_URL}`} target="_blank" rel="noopener noreferrer">
                    <CardMedia image={`${REACT_APP_LOGO_IMAGE_URL}`} className={classes.logo} />
                </a>
            )}

            {showDashboard(context) && (
                <DashboardMenuItem
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                    name="Dashboard"
                />
            )}

            {sections.map(section => (
                <SubMenu
                    key={section.key + section.name}
                    handleToggle={() => handleToggle(section.key)}
                    isOpen={!!openSections[section.key]}
                    sidebarIsOpen={open}
                    name={section.name}
                    icon={<section.icon />}
                    dense={dense}
                >
                    {section.items.map(item =>
                        section.external ? (
                            <React.Fragment key={item.key}>
                                <Divider />
                                <IconLink
                                    url={item.url}
                                    text={item.label}
                                    target="_blank"
                                    sidebarIsOpen={open}
                                />
                            </React.Fragment>
                        ) : (
                            <MenuItemLink
                                key={item.key}
                                to={item.to}
                                primaryText={item.label}
                                leftIcon={<item.icon />}
                                onClick={onMenuClick}
                                sidebarIsOpen={open}
                                dense={dense}
                            />
                        )
                    )}
                </SubMenu>
            ))}

            {Number(level) > 1 && !isXsmall && logout}
            {isXsmall && (
                <MenuItemLink
                    to="/configuration"
                    primaryText={translate('pos.configuration')}
                    leftIcon={<SettingsIcon />}
                    onClick={onMenuClick}
                    dense={dense}
                />
            )}
            {Number(level) > 1 && isXsmall && logout}
        </div>
    );
};

Menu.propTypes = {
    onMenuClick: PropTypes.func,
    logout: PropTypes.object,
};

export default Menu;
